import { com } from '@eidu/web'
import { Button, iconSizes, Stack } from '@common/ui'
import { IconPlus } from '@common/ui/icons'
import React, { useMemo, useState } from 'react'
import Collapsable from './Collapsable'
import SemanticLinksItem from '../input/SemanticLinksItem'
import AddSemanticLinkForm from '../input/AddSemanticLinkForm'
import AddOtherSemanticLinkForm from '../input/AddOtherSemanticLinkForm'
import { predefinedConfigsForKind } from '../input/useCustomSemanticLinkKey'
import { isReferenceType } from '../misc/ReferenceTypeUtil'
import SemanticLinkConfig, { fromPredefined } from '../../types/SemanticLinkConfig'
import EntityType = com.eidu.entity.type.EntityType
import SemanticLink = com.eidu.entity.analytics.SemanticLink
import SemanticLinkKey = com.eidu.entity.analytics.SemanticLinkKey
import allSemanticLinkKeyConfigurations = com.eidu.entity.analytics.allSemanticLinkKeyConfigurations
import FieldType = com.eidu.entity.field.FieldType

const fieldTypeOfLink = (link: SemanticLink, entityTypes: readonly EntityType[]): FieldType => {
  const lastEdge = link.path.edges.asJsReadonlyArrayView().at(-1)
  if (!lastEdge) return FieldType.Reference
  const targetType = entityTypes.find(({ id }) => id.equals(lastEdge.nextType))
  const field = targetType?.fields.asJsReadonlyArrayView().find(({ id }) => id.equals(lastEdge.field))
  return field && !isReferenceType(field.type) ? field.type : FieldType.Reference
}

const configForKey = (key: SemanticLinkKey, links: readonly SemanticLink[], entityTypes: readonly EntityType[]) => {
  const predefined = allSemanticLinkKeyConfigurations.asJsReadonlyArrayView().find((c) => c.key.equals(key))
  if (predefined) return fromPredefined(predefined)
  const config: SemanticLinkConfig = {
    title: key.toString(),
    key,
    toTypeKind: null,
    duplicatesAllowed: true,
    fieldType: fieldTypeOfLink(links.find((link) => link.key.equals(key))!, entityTypes),
  }
  return config
}

type SemanticLinksStackProps = {
  entityTypes: readonly EntityType[]
  entityType: EntityType
  semanticLinks: readonly SemanticLink[]
}

type AddFormProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  onSave: () => void
  onCancel: () => void
}

const AddForm = (props: AddFormProps) =>
  predefinedConfigsForKind(props.entityType.kind).length > 0 ? (
    <AddSemanticLinkForm {...props} />
  ) : (
    <AddOtherSemanticLinkForm {...props} />
  )

const SemanticLinksStack = ({ entityTypes, entityType, semanticLinks }: SemanticLinksStackProps) => {
  const [showAddForm, setShowAddForm] = useState(false)

  const configs = useMemo(() => {
    const linksForType = semanticLinks.filter(({ path }) => path.fromType.equals(entityType.id))
    const uniqueKeys = [...new Map(linksForType.map((link) => [link.key.toString(), link.key])).values()]
    return uniqueKeys.map((key) => configForKey(key, linksForType, entityTypes))
  }, [semanticLinks, entityType.id, entityTypes])

  return (
    <Collapsable title={entityType.name} key={entityType.id.toString()}>
      <Stack gap="xs">
        {configs.map((config) => (
          <SemanticLinksItem
            key={config.key.toString()}
            entityType={entityType}
            entityTypes={entityTypes}
            semanticLinks={semanticLinks}
            semanticLinkConfig={config}
          />
        ))}
        {showAddForm ? (
          <AddForm
            entityType={entityType}
            entityTypes={entityTypes}
            onSave={() => setShowAddForm(false)}
            onCancel={() => setShowAddForm(false)}
          />
        ) : (
          <Button
            variant="outline"
            leftSection={<IconPlus size={iconSizes.sm} stroke={1.5} />}
            onClick={() => setShowAddForm(true)}
          >
            Add semantic link
          </Button>
        )}
      </Stack>
    </Collapsable>
  )
}

export default SemanticLinksStack
